"use client";

import { memo, useCallback, useState } from "react";

import { Badge } from "@vllnt/ui";
import Link from "next/link";
import { usePathname } from "next/navigation";

const MAX_VISIBLE_DESKTOP = 10;
const MAX_VISIBLE_MOBILE = 5;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/[\u0300-\u036F]/g, "")
    .replaceAll(/[^\s\w-]/g, "")
    .trim()
    .replaceAll(/\s+/g, "-")
    .replaceAll(/-+/g, "-")
    .replaceAll(/^-+|-+$/g, "");
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type ExpandableCategoryFilterProps = {
  categories: string[];
  lang: string;
};

function CategoryBadge({
  category,
  desktopOnly,
  href,
  isSelected,
}: {
  category: string;
  desktopOnly: boolean;
  href: string;
  isSelected: boolean;
}): React.ReactNode {
  const hideClass = desktopOnly ? " hidden sm:inline-flex" : "";

  if (isSelected) {
    return (
      <Badge
        className={`cursor-default${hideClass}`}
        key={category}
        variant="default"
      >
        {capitalize(category)}
      </Badge>
    );
  }

  return (
    <Link className={hideClass || undefined} href={href} key={category}>
      <Badge
        className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
        variant="secondary"
      >
        {capitalize(category)}
      </Badge>
    </Link>
  );
}

function ExpandableCategoryFilterImpl({
  categories,
  lang,
}: ExpandableCategoryFilterProps): React.ReactNode {
  const pathname = usePathname();
  const [expanded, setExpanded] = useState(false);

  const allCategories = [...new Set(categories)].toSorted();

  const handleToggle = useCallback(() => {
    setExpanded((previous) => !previous);
  }, []);

  if (allCategories.length === 0) return null;

  const hasMobileOverflow = allCategories.length > MAX_VISIBLE_MOBILE;
  const hasDesktopOverflow = allCategories.length > MAX_VISIBLE_DESKTOP;
  const visible = expanded
    ? allCategories
    : allCategories.slice(0, MAX_VISIBLE_DESKTOP);

  return (
    <div className="flex flex-wrap gap-2">
      {visible.map((category, index) => (
        <CategoryBadge
          category={category}
          desktopOnly={!expanded && index >= MAX_VISIBLE_MOBILE}
          href={`/${lang}/${slugify(category)}`}
          isSelected={pathname.includes(`/${slugify(category)}`)}
          key={category}
        />
      ))}
      {!expanded && hasMobileOverflow ? (
        <Badge
          className="cursor-pointer hover:bg-muted transition-colors sm:hidden"
          onClick={handleToggle}
          variant="outline"
        >
          {`+${allCategories.length - MAX_VISIBLE_MOBILE}`}
        </Badge>
      ) : undefined}
      {!expanded && hasDesktopOverflow ? (
        <Badge
          className="cursor-pointer hover:bg-muted transition-colors hidden sm:inline-flex"
          onClick={handleToggle}
          variant="outline"
        >
          {`+${allCategories.length - MAX_VISIBLE_DESKTOP}`}
        </Badge>
      ) : undefined}
      {expanded && hasMobileOverflow ? (
        <Badge
          className="cursor-pointer hover:bg-muted transition-colors"
          onClick={handleToggle}
          variant="outline"
        >
          −
        </Badge>
      ) : undefined}
    </div>
  );
}

export const ExpandableCategoryFilter = memo(ExpandableCategoryFilterImpl);
ExpandableCategoryFilter.displayName = "ExpandableCategoryFilter";
